import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, ApiError, type OAuthProviderId } from "@/services/api";
import {
  OAUTH_POPUP_MESSAGE,
  OAUTH_SILENT_STATE,
  isSilentFallbackOAuthError,
  postOAuthResultToOpener,
} from "@/utils/oauthPopup";
import { recordOAuthProviderLogin } from "@/utils/oauthSession";

type CallbackStatus = "processing" | "success" | "error";

const OAUTH_PROVIDERS: OAuthProviderId[] = ["discord", "feedforward"];

const PROVIDER_LABELS: Record<OAuthProviderId, string> = {
  discord: "Discord",
  feedforward: "Feedforward",
};

function isOAuthProviderId(value: string | undefined): value is OAuthProviderId {
  return value !== undefined && OAUTH_PROVIDERS.includes(value as OAuthProviderId);
}

function isOAuthPopupFlow(): boolean {
  return Boolean(window.opener && !window.opener.closed);
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { provider: providerParam } = useParams<{ provider: string }>();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [status, setStatus] = useState<CallbackStatus>("processing");
  const [error, setError] = useState<string | null>(null);
  const [isPopupCallback, setIsPopupCallback] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;

    if (!isOAuthProviderId(providerParam)) {
      setStatus("error");
      setError("Unknown authentication provider");
      return;
    }

    const provider = providerParam;
    const providerLabel = PROVIDER_LABELS[provider];
    const popupFlow = isOAuthPopupFlow();
    if (popupFlow) {
      setIsPopupCallback(true);
    }

    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    const stateParam = searchParams.get("state");
    const wasSilentAttempt = stateParam === OAUTH_SILENT_STATE;

    const notifyPopupOpener = (
      errorMessage: string,
      silentFallback = false,
    ) => {
      postOAuthResultToOpener({
        type: OAUTH_POPUP_MESSAGE,
        status: "error",
        provider,
        error: errorMessage,
        silentFallback,
      });
      window.close();
    };

    if (errorParam) {
      const message = errorDescription || `OAuth error: ${errorParam}`;
      const silentFallback =
        wasSilentAttempt || isSilentFallbackOAuthError(errorParam);
      if (popupFlow) {
        processedRef.current = true;
        notifyPopupOpener(message, silentFallback);
        return;
      }
      setStatus("error");
      setError(message);
      return;
    }

    if (!code) {
      const message = "No authorization code received";
      if (popupFlow) {
        processedRef.current = true;
        notifyPopupOpener(message, wasSilentAttempt);
        return;
      }
      setStatus("error");
      setError(message);
      return;
    }

    processedRef.current = true;

    if (popupFlow) {
      const sent = postOAuthResultToOpener({
        type: OAUTH_POPUP_MESSAGE,
        status: "success",
        provider,
        code,
      });
      if (sent) {
        setStatus("success");
        window.setTimeout(() => window.close(), 400);
      } else {
        setStatus("error");
        setError("Could not complete sign in with the main window");
      }
      return;
    }

    const handleCallback = async () => {
      try {
        const response = await api.loginWithOAuth(provider, code);

        login(response.access_token, response.user);
        recordOAuthProviderLogin(provider);

        try {
          const workspacesResponse = await api.listWorkspaces();
          if (workspacesResponse.workspaces.length > 0) {
            const firstWorkspace = workspacesResponse.workspaces[0];
            localStorage.setItem("active_workspace_id", firstWorkspace.id);
          }
        } catch (workspaceErr) {
          console.warn("Failed to fetch workspaces:", workspaceErr);
        }

        setStatus("success");

        setTimeout(() => {
          navigate("/analysis", { replace: true });
        }, 1000);
      } catch (err) {
        setStatus("error");
        if (err instanceof ApiError) {
          setError(
            err.message || `Failed to authenticate with ${providerLabel}`,
          );
        } else {
          setError("An unexpected error occurred");
        }
      }
    };

    handleCallback();
  }, [searchParams, login, navigate, providerParam]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#063F6B] via-[#124C77] to-[#042a4d] flex items-center justify-center p-4">
      <motion.div
        className="bg-[#063F6B]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl max-w-md w-full text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        {status === "processing" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className="text-xl text-white font-medium mb-2">
              Signing you in...
            </h2>
            <p className="text-slate-400 text-sm">
              Please wait while we complete your authentication
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            </motion.div>
            <h2 className="text-xl text-white font-medium mb-2">
              {isPopupCallback ? "Sign in complete" : "Welcome!"}
            </h2>
            <p className="text-slate-400 text-sm">
              {isPopupCallback
                ? "This window will close automatically..."
                : "Redirecting to your dashboard..."}
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl text-white font-medium mb-2">
              Authentication Failed
            </h2>
            <p className="text-red-400 text-sm mb-6">{error}</p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-lg transition-colors cursor-pointer"
            >
              Back to Login
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
}
